import { FormField } from '@workspace/ui/components/form-field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { TextInput } from '@workspace/ui/components/text-input'
import { Textarea } from '@workspace/ui/components/textarea'
import { CalendarDays } from 'lucide-react'
import {
  CurrencyInput,
  FundraisingToggle,
  PersonalInfoSection,
  TagsInput,
} from './confirm-form-fields'
import type { FounderFormData, FounderStartupInfo } from './confirm-form-utils'
import { parseToMonthInput } from './confirm-form-utils'

interface FounderConfirmFormProps {
  data: FounderFormData
  disabled?: boolean
  categories: { id: string; name: string }[]
  productStages: { value: string; label: string }[]
  allTags: string[]
  onChange: (data: FounderFormData) => void
}

const FUNDRAISING_STAGE_OPTIONS = [
  { value: 'PRE_SEED', label: 'Pre-Seed' },
  { value: 'SEED', label: 'Seed' },
]

function matchStageLabel(value: string, options: { value: string; label: string }[]): string {
  const matched = options.find(
    (s) =>
      s.value.toLowerCase() === value.toLowerCase() || s.label.toLowerCase() === value.toLowerCase()
  )
  return matched?.label ?? value
}

export function FounderConfirmForm({
  data,
  disabled,
  categories,
  productStages,
  allTags,
  onChange,
}: FounderConfirmFormProps) {
  const setPersonal = (personal: FounderFormData['personal']) => {
    onChange({ ...data, personal })
  }

  const setStartup = <K extends keyof FounderStartupInfo>(
    field: K,
    value: FounderStartupInfo[K]
  ) => {
    onChange({ ...data, startup: { ...data.startup, [field]: value } })
  }

  const productStageValue = matchStageLabel(data.startup.productStage, productStages)
  const fundraisingStageValue = matchStageLabel(
    data.startup.fundraisingStage,
    FUNDRAISING_STAGE_OPTIONS
  )

  return (
    <>
      <PersonalInfoSection data={data.personal} disabled={disabled} onChange={setPersonal} />

      <div className="p-4">
        <h4 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Startup
        </h4>
        <div className="space-y-3">
          <FormField label="Startup Name">
            <TextInput
              inputSize="sm"
              value={data.startup.startupName}
              onChange={(e) => setStartup('startupName', e.target.value)}
              disabled={disabled}
            />
          </FormField>

          <FormField label="Description">
            <Textarea
              textareaSize="sm"
              value={data.startup.description}
              onChange={(e) => setStartup('description', e.target.value)}
              rows={3}
              disabled={disabled}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Category">
              {categories.length > 0 ? (
                <Select
                  value={data.startup.category}
                  onValueChange={(v) => setStartup('category', v)}
                  disabled={disabled}
                >
                  <SelectTrigger size="sm" className="text-xs">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <TextInput
                  inputSize="sm"
                  value={data.startup.category}
                  onChange={(e) => setStartup('category', e.target.value)}
                  disabled={disabled}
                />
              )}
            </FormField>
            <FormField label="Startup Website">
              <TextInput
                inputSize="sm"
                value={data.startup.startupWebsite}
                onChange={(e) => setStartup('startupWebsite', e.target.value)}
                disabled={disabled}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Product Stage">
              {productStages.length > 0 ? (
                <Select
                  value={productStageValue}
                  onValueChange={(v) => setStartup('productStage', v)}
                  disabled={disabled}
                >
                  <SelectTrigger size="sm" className="text-xs">
                    <SelectValue placeholder="Select product stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {productStages.map((stage) => (
                      <SelectItem key={stage.value} value={stage.label}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <TextInput
                  inputSize="sm"
                  value={data.startup.productStage}
                  onChange={(e) => setStartup('productStage', e.target.value)}
                  disabled={disabled}
                />
              )}
            </FormField>
            <FormField label="ARR">
              <CurrencyInput
                value={data.startup.arr}
                onChange={(v) => setStartup('arr', v)}
                disabled={disabled}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Founded">
              <div className="relative">
                <TextInput
                  inputSize="sm"
                  type="month"
                  value={parseToMonthInput(data.startup.founded)}
                  onChange={(e) => setStartup('founded', e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker()}
                  disabled={disabled}
                  className="cursor-pointer pr-9 [&::-webkit-calendar-picker-indicator]:hidden"
                />
                <CalendarDays className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              </div>
            </FormField>
          </div>

          <FormField label="Tags">
            <TagsInput
              value={data.startup.tags}
              onChange={(v) => setStartup('tags', v)}
              suggestions={allTags}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="North Star Metric Name">
              <TextInput
                inputSize="sm"
                value={data.startup.northStarMetricName}
                onChange={(e) => setStartup('northStarMetricName', e.target.value)}
                disabled={disabled}
              />
            </FormField>
            <FormField label="North Star Metric Value">
              <TextInput
                inputSize="sm"
                value={data.startup.northStarMetricValue}
                onChange={(e) => setStartup('northStarMetricValue', e.target.value)}
                disabled={disabled}
              />
            </FormField>
          </div>

          <div className="border-t border-border" />

          <FormField label="Are you fundraising?">
            <FundraisingToggle
              value={data.startup.isFundraising}
              onChange={(v) => setStartup('isFundraising', v)}
            />
          </FormField>

          {data.startup.isFundraising && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Fundraising Stage">
                <Select
                  value={fundraisingStageValue}
                  onValueChange={(v) => setStartup('fundraisingStage', v)}
                  disabled={disabled}
                >
                  <SelectTrigger size="sm" className="text-xs">
                    <SelectValue placeholder="Select fundraising stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNDRAISING_STAGE_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.label}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Fundraising Amount">
                <CurrencyInput
                  value={data.startup.fundraisingAmount}
                  onChange={(v) => setStartup('fundraisingAmount', v)}
                  disabled={disabled}
                />
              </FormField>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
